import Services from "../services/Services";

export const securexUrls = {
  nam: "https://securex.us.security.cisco.com",
  eu: "https://securex.eu.security.cisco.com",
  apjc: "https://securex.apjc.security.cisco.com",
  int: "https://securex.int.iroh.site",
  test: "https://securex.test.iroh.site"
};

const xdrUrls = {
  nam: "https://xdr.us.security.cisco.com",
  eu: "https://xdr.eu.security.cisco.com",
  apjc: "https://xdr.apjc.security.cisco.com",
  int: "https://xdr.int.iroh.site",
  test: "https://xdr.test.iroh.site"
};

export const docUrls = {
  prod: "https://docs.securex.security.cisco.com",
  int: "https://docs.int.iroh.site",
  test: "https://docs.test.iroh.site"
};

export const observableTypeMap = {
  email: ["email_messageid", "cisco_mid"],
  email_service: ["process.email_services"],
  filehash: ["sha256", "sha1", "md5"],
  path: ["file_path"],
  host: ["amp_computer_guid", "hostname"],
  ip: ["odns_identity", "ipv6"]
};

export const mapObservableType = type => {
  for (let [key, value] of Object.entries(observableTypeMap)) {
    if (value.includes(type)) {
      return key;
    }
  }

  return type;
};

const availableSprites = {};
const hasSprite = mappedType => {
  if (availableSprites.hasOwnProperty(mappedType)) {
    return availableSprites[mappedType];
  }

  const sprites = document.getElementById("ats-ribbon-sprite"),
    nodes = sprites.childNodes[0].childNodes[0].childNodes;

  let found = false;
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === `ats-ribbon-${mappedType}`) {
      found = true;
      break;
    }
  }

  availableSprites[mappedType] = found;

  return found;
};

export const observableSprite = type => {
  const mappedType = mapObservableType(type);

  if (hasSprite(mappedType)) {
    return `ats-ribbon-${mappedType}`;
  }

  return `ats-ribbon-generic-node`;
};

export const dispositionClass = disposition => {
  const key = dispositionKey(disposition);

  return `ats-observable-disposition-${key}`;
};

export const dispositionKey = disposition => {
  disposition =
    typeof disposition === "string" ? parseInt(disposition) : disposition;

  switch (disposition) {
    case 1:
      return "clean";
    case 2:
      return "malicious";
    case 3:
      return "suspicious";
    case 4:
      return "common";
    default:
      return "unknown";
  }
};

export const xdrHref = () => {
  if (!Services.host) {
    return null;
  }

  const region = hostRegion();

  return xdrUrls[region];
};

export const baseHref = () => {
  // Check for the main UI HREF, which is currently XDR
  const mainUIHref = xdrHref();

  if (mainUIHref) {
    return mainUIHref;
  }

  //Failing that, fallback to the config
  const base = Services.uiHost || Services.apiHost;

  return base ? base.replace(/\/$/, "") : base;
};

export const securexHref = () => {
  if (!Services.host) {
    return null;
  }

  const region = hostRegion();

  if (Services.host.includes("visibility")) {
    return securexUrls[region];
  }

  return Services.host;
};

export const securexDocsHref = () => {
  if (Services.host.includes(".int.")) {
    return docUrls.int;
  } else if (Services.host.includes(".test.")) {
    return docUrls.test;
  }

  return docUrls.prod;
};

export const brandAssetsHref = () => {
  if (Services.cdnDomain) {
    return `https://brand-assets.${Services.cdnDomain}`;
  }

  return "https://brand-assets.security.cisco.com/";
};

export const hostRegion = () => {
  const parser = document.createElement("a");
  parser.href = Services.host;

  const split = parser.host.split(".");
  split.shift();

  const region = split[0];

  if (region === "amp") {
    return "nam";
  }

  if (!Object.keys(securexUrls).includes(region)) {
    return "nam";
  }

  return region;
};

export const investigateObservablesHref = observable => {
  const base = baseHref(),
    observables = Array.isArray(observable) ? observable : [observable];

  const query = observables.map(o => {
    return `${o.type}:"${o.value}"`;
  });

  return `${base}/investigate?q=${encodeURIComponent(query.join("\n"))}`;
};

export const investigateCaseHref = caseId => {
  if (!caseId) {
    return;
  }

  const id = caseId.replace("casebook-", "");

  return `${baseHref()}/investigate?spid=${id}`;
};

export const incidentHref = incidentId => {
  if (!incidentId) {
    return;
  }

  const incidentManagerURL = `${baseHref()}/incidents/${scrubId(incidentId)}`;

  return incidentManagerURL;
};

export const scrubId = id => {
  if (!id) {
    return;
  }
  const split = id.split("/");

  if (split.length > 1) {
    return split[split.length - 1];
  } else {
    return id;
  }
};

export const hashCode = s => {
  let hash = 0,
    i,
    l,
    char;
  if (s.length == 0) {
    return hash;
  }
  for (i = 0, l = s.length; i < l; i++) {
    char = s.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return `hashedId-${Math.abs(hash)}`;
};

// https://stackoverflow.com/questions/17267329/converting-unicode-character-to-string-format
export const unicodeToChar = text => {
  if (!text || !text.replace) {
    return text;
  }

  return text.replace(/\\u[\dA-F]{4}/gi, function(match) {
    return String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16));
  });
};

export const prettyPrintVariables = variables => {
  const output = [];

  Object.keys(variables)
    .filter(key => {
      return key.includes("q_json");
    })
    .forEach(key => {
      const jsonArray = JSON.stringify(
        {
          // unescape the first layer for pretty printing
          [key]: JSON.parse(variables[key].replace(/(^")|("$)/g, ""))
        },
        null,
        2
      ).split("\n");

      if (jsonArray.length > 2) {
        const secondToLast = jsonArray.length - 2;
        // re-escape the first layer but leave indentation
        jsonArray[1] = jsonArray[1].replace(/\{/, '"{');
        jsonArray[secondToLast] = jsonArray[secondToLast].replace(/\}/, '}"');
      }

      const foo = jsonArray.join("\n");
      output.push(foo);
    });

  return output.join(",\n");
};
